import { X, Trash2, AlertCircle } from 'lucide-react';
import { colors } from '../styles/colors';
import { formatEuro } from '../utils/formatters';
import { usePaymentHistory } from '../hooks/usePaymentHistory';
import ModalPortal from './ModalPortal';
import PaymentTable from './payments/PaymentTable';
import PaymentForm from './payments/PaymentForm';

/**
 * PaymentHistoryModal - Modale d'historique des paiements
 *
 * Refactorisé pour utiliser:
 * - usePaymentHistory: hook de gestion de l'état et des handlers
 * - PaymentTable: tableau des paiements avec édition
 * - PaymentForm: formulaire d'ajout de paiement
 */
function PaymentHistoryModal({ isOpen, onClose, facture, onPaymentAdded }) {
  const {
    payments,
    factureInfo,
    loading,
    saving,
    montantTTC,
    resteAPayerEffectif,
    isFacturePayee,
    isFacturePayeeSansHistorique,
    editingPaymentId,
    deleteConfirmPaymentId,
    setDeleteConfirmPaymentId,
    formData,
    updateFormField,
    handleAddPayment,
    handleEditPayment,
    handleSaveEdit,
    handleDeletePayment,
    handleClose
  } = usePaymentHistory(facture, isOpen, onClose, onPaymentAdded);

  if (!isOpen) return null;

  return (
    <>
      <ModalPortal>
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
            fontFamily: '"Noto Sans", sans-serif'
          }}
          onClick={handleClose}
        >
          <div
            style={{
              backgroundColor: colors.text.white,
              borderRadius: '0.75rem',
              boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1)',
              width: '95%',
              maxWidth: '900px',
              maxHeight: '90vh',
              overflow: 'hidden',
              display: 'flex',
              flexDirection: 'column'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <ModalHeader
              factureInfo={factureInfo}
              facture={facture}
              saving={saving}
              onClose={handleClose}
            />

            {/* Body */}
            <div style={{ padding: '1.5rem', overflowY: 'auto', flex: 1 }}>
              {loading ? (
                <LoadingState />
              ) : (
                <>
                  {/* Summary Cards */}
                  <SummaryCards
                    montantTTC={montantTTC}
                    resteAPayerEffectif={resteAPayerEffectif}
                  />

                  {/* Payment History Table */}
                  <PaymentTable
                    payments={payments}
                    isFacturePayee={isFacturePayee}
                    isFacturePayeeSansHistorique={isFacturePayeeSansHistorique}
                    editingPaymentId={editingPaymentId}
                    formData={formData}
                    onFormChange={updateFormField}
                    onEdit={handleEditPayment}
                    onSave={handleSaveEdit}
                    onDelete={setDeleteConfirmPaymentId}
                    saving={saving}
                  />

                  {/* Add Payment Form */}
                  {resteAPayerEffectif > 0 && !editingPaymentId && (
                    <PaymentForm
                      formData={formData}
                      onFormChange={updateFormField}
                      onSubmit={handleAddPayment}
                      saving={saving}
                    />
                  )}
                </>
              )}
            </div>

            {/* Footer */}
            <ModalFooter saving={saving} onClose={handleClose} />
          </div>
        </div>
      </ModalPortal>

      {/* Delete Confirmation Modal */}
      {deleteConfirmPaymentId && (
        <DeleteConfirmModal
          saving={saving}
          onCancel={() => setDeleteConfirmPaymentId(null)}
          onConfirm={handleDeletePayment}
        />
      )}
    </>
  );
}

// === SOUS-COMPOSANTS ===

function ModalHeader({ factureInfo, facture, saving, onClose }) {
  return (
    <div style={{
      padding: '1.5rem',
      borderBottom: `1px solid ${colors.secondary.lightGray}`,
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center'
    }}>
      <div>
        <h2 style={{
          margin: 0,
          fontSize: '1.5rem',
          fontWeight: '600',
          color: colors.primary.gray
        }}>
          Paiements - {factureInfo?.['N° facture']}
        </h2>
        <p style={{
          margin: '0.5rem 0 0 0',
          fontSize: '0.875rem',
          color: colors.secondary.mediumGray
        }}>
          {facture?.consultantNom} - {facture?.fields?.Trimestre} {facture?.fields?.Année}
        </p>
      </div>
      <button
        onClick={onClose}
        disabled={saving}
        style={{
          background: 'none',
          border: 'none',
          cursor: saving ? 'not-allowed' : 'pointer',
          padding: '0.5rem',
          color: colors.secondary.mediumGray,
          display: 'flex'
        }}
      >
        <X size={24} />
      </button>
    </div>
  );
}

function LoadingState() {
  return (
    <div style={{
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      padding: '2rem',
      color: colors.secondary.mediumGray
    }}>
      Chargement...
    </div>
  );
}

function SummaryCards({ montantTTC, resteAPayerEffectif }) {
  return (
    <div style={{
      display: 'grid',
      gridTemplateColumns: 'repeat(2, 1fr)',
      gap: '1rem',
      marginBottom: '2rem'
    }}>
      <div style={{
        padding: '1.5rem',
        backgroundColor: '#dbeafe',
        borderRadius: '0.5rem',
        textAlign: 'center'
      }}>
        <div style={{ fontSize: '0.875rem', color: colors.text.secondary, marginBottom: '0.5rem' }}>
          Montant TTC
        </div>
        <div style={{ fontSize: '1.5rem', fontWeight: '700', color: colors.primary.gray }}>
          {formatEuro(montantTTC)}
        </div>
      </div>

      <div style={{
        padding: '1.5rem',
        backgroundColor: resteAPayerEffectif === 0 ? '#d1fae5' : '#fef3c7',
        borderRadius: '0.5rem',
        textAlign: 'center'
      }}>
        <div style={{ fontSize: '0.875rem', color: colors.text.secondary, marginBottom: '0.5rem' }}>
          Reste à payer
        </div>
        <div style={{ fontSize: '1.5rem', fontWeight: '700', color: colors.primary.gray }}>
          {formatEuro(resteAPayerEffectif)} {resteAPayerEffectif === 0 && '✓'}
        </div>
      </div>
    </div>
  );
}

function ModalFooter({ saving, onClose }) {
  return (
    <div style={{
      padding: '1.5rem',
      borderTop: `1px solid ${colors.secondary.lightGray}`,
      display: 'flex',
      justifyContent: 'flex-end'
    }}>
      <button
        onClick={onClose}
        disabled={saving}
        style={{
          padding: '0.75rem 1.5rem',
          backgroundColor: colors.text.white,
          color: colors.primary.gray,
          border: `2px solid ${colors.secondary.lightGray}`,
          borderRadius: '0.5rem',
          fontSize: '0.875rem',
          fontWeight: '500',
          cursor: saving ? 'not-allowed' : 'pointer'
        }}
      >
        Fermer
      </button>
    </div>
  );
}

function DeleteConfirmModal({ saving, onCancel, onConfirm }) {
  return (
    <ModalPortal>
      <div
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 10001
        }}
        onClick={onCancel}
      >
        <div
          onClick={(e) => e.stopPropagation()}
          style={{
            backgroundColor: colors.text.white,
            borderRadius: '0.75rem',
            boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1)',
            maxWidth: '400px',
            width: '90%',
            overflow: 'hidden'
          }}
        >
          {/* Header */}
          <div style={{
            padding: '1.5rem',
            backgroundColor: '#fef2f2',
            borderBottom: '1px solid #fecaca'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
              <div style={{
                padding: '0.5rem',
                backgroundColor: '#fee2e2',
                borderRadius: '0.5rem',
                display: 'flex'
              }}>
                <AlertCircle size={24} color="#dc2626" />
              </div>
              <h3 style={{
                margin: 0,
                fontSize: '1.125rem',
                fontWeight: '600',
                color: '#991b1b'
              }}>
                Confirmer la suppression
              </h3>
            </div>
          </div>

          {/* Body */}
          <div style={{ padding: '1.5rem' }}>
            <p style={{
              margin: 0,
              color: colors.text.darkGray,
              fontSize: '0.875rem',
              lineHeight: '1.5'
            }}>
              Êtes-vous sûr de vouloir supprimer ce paiement ? Cette action est irréversible et mettra à jour le statut de la facture.
            </p>
          </div>

          {/* Footer */}
          <div style={{
            padding: '1.5rem',
            backgroundColor: colors.secondary.lightGray,
            display: 'flex',
            gap: '0.75rem',
            justifyContent: 'flex-end'
          }}>
            <button
              onClick={onCancel}
              disabled={saving}
              style={{
                padding: '0.625rem 1.25rem',
                backgroundColor: colors.text.white,
                color: colors.text.darkGray,
                border: `2px solid ${colors.secondary.mediumGray}`,
                borderRadius: '0.5rem',
                fontSize: '0.875rem',
                fontWeight: '500',
                cursor: saving ? 'not-allowed' : 'pointer',
                opacity: saving ? 0.5 : 1
              }}
            >
              Annuler
            </button>
            <button
              onClick={onConfirm}
              disabled={saving}
              style={{
                padding: '0.625rem 1.25rem',
                backgroundColor: '#dc2626',
                color: colors.text.white,
                border: 'none',
                borderRadius: '0.5rem',
                fontSize: '0.875rem',
                fontWeight: '600',
                cursor: saving ? 'not-allowed' : 'pointer',
                opacity: saving ? 0.5 : 1,
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem'
              }}
            >
              <Trash2 size={16} />
              {saving ? 'Suppression...' : 'Supprimer'}
            </button>
          </div>
        </div>
      </div>
    </ModalPortal>
  );
}

export default PaymentHistoryModal;
